import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAdvisorLeadsUrl } from "../../utils/urls";
import AddLeadPopup from "./AddLeadPopup";
import { useSelector } from "react-redux";
import { FiEye } from "react-icons/fi";
import AdvisorConditionsPage from "../common/AdvisorConditionsPage";
import AdvisorConditionsModal from "../common/AdvisorConditionsModal";
import ServerPaginationDataGrid from "../../components/common/CustomDataGrid/ServerPaginationDataGrid";
import AddLeadModal from "../common/AddLeadModal/AddLeadModal";
import GridListToggle from "../../components/common/GridListToggle";
import InfiniteScrollWrapper from "../../components/common/InfiniteScrollWrapper";

const LeadsTab = () => {
  const loggedInUser = useSelector((state) => state.auth);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [viewMode, setViewMode] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (window.innerWidth <= 600) {
      setViewMode("grid");
    } else {
      setViewMode("list");
    }
  }, []);


  const handleViewLead = (lead) => {
    navigate(`/leads/details/${lead.lead_product_relation_display_id}`);
  };

  const PriorityColorEnum = {
    High: "text-red-700",
    Medium: "text-yellow-700",
    Low: "text-green-700",
  };
  const StatusColorEnum = {
    Sold: "bg-red-100 text-red-700",
    Lost: "bg-yellow-100 text-yellow-700",
    New: "bg-green-100 text-green-700",
    Assigned: "bg-orange-100 text-orange-700",
  };

  const columns = [
    { field: "lead_name", headerName: "Lead Name" },
    { field: "lead_product_relation_display_id", headerName: "Id" },
    {
      field: "product_name",
      headerName: "Product Interest",
      renderCell: (row) => {
        return (
          <span className={`px-2 py-1 rounded-md text-sm font-medium`}>
            {row?.product_name ||
              row?.sub_category_title ||
              row?.category_title ||
              "N/A"}
          </span>
        );
      },
    },
    { field: "company_name", headerName: "Company" },
    {
      field: "lead_status_title",
      headerName: "Status",
      flex: 1,
      renderCell: (row) => {
        const statusValue = row.lead_status_title;
        const statusColor =
          StatusColorEnum[statusValue] || "bg-gray-200 text-gray-800";
        return (
          <span
            className={`px-2 py-1 rounded-md text-sm font-medium ${statusColor}`}
          >
            {statusValue || "N/A"}
          </span>
        );
      },
    },
    {
      field: "priority",
      headerName: "Priority",
      flex: 1,
      renderCell: (row) => {
        const priorityValue = row.priority;
        const priorityColor =
          PriorityColorEnum[priorityValue] || "bg-gray-200 text-gray-800";
        return (
          <span className={`text-sm font-medium ${priorityColor}`}>
            {priorityValue || "N/A"}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (row) => (
        <div
          className="text-sm border-r border-gray-200 cursor-pointer hover:underline"
          onClick={() => handleViewLead(row)}
        >
          <span className="flex items-center gap-1 text-[#0184FF] underline decoration-[#0184FF]">
            <FiEye className="w-4 h-4 inline-block text-[#0184FF]" />
            View
          </span>
        </div>
      ),
    },
  ];

const LeadCard = ({ data }) => {
  const navigate = useNavigate();

  const PriorityColorEnum = {
    High: "text-red-700",
    Medium: "text-yellow-700",
    Low: "text-green-700",
  };
  const StatusColorEnum = {
    Sold: "bg-red-100 text-red-700",
    Lost: "bg-yellow-100 text-yellow-700",
    New: "bg-green-100 text-green-700",
    Assigned: "bg-orange-100 text-orange-700",
  };

  const statusColor =
    StatusColorEnum[data?.lead_status_title] ||
    "bg-gray-200 text-gray-800";
  const priorityColor =
    PriorityColorEnum[data?.priority] || "text-gray-800";

  return (
    <div className="border border-gray-200 rounded-lg shadow-sm bg-white p-4 flex flex-col gap-3 hover:shadow-md transition-shadow duration-200">
      {/* Lead Name & ID */}
      <div className="flex flex-col">
        <h2 className="text-lg font-semibold text-gray-900">
          {data?.lead_name || "N/A"}
        </h2>
        <span className="text-sm text-gray-500">
          ID: {data?.lead_product_relation_display_id || "N/A"}
        </span>
      </div>

      {/* Product */}
      <div className="text-sm text-gray-700">
        <span className="font-medium">Product:</span>{" "}
        {data?.product_name ||
          data?.sub_category_title ||
          data?.category_title ||
          "N/A"}
      </div>

      {/* Company */}
      <div className="text-sm text-gray-700">
        <span className="font-medium">Company:</span>{" "}
        {data?.company_name || "N/A"}
      </div>

      {/* Status & Priority */}
      <div className="flex items-center gap-3 mt-1">
        <span
          className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor}`}
        >
          {data?.lead_status_title || "N/A"}
        </span>
        <span className={`text-xs font-medium ${priorityColor}`}>
          {data?.priority || "N/A"}
        </span>
      </div>

      {/* View Button */}
      <button
        onClick={() =>
          navigate(`/leads/details/${data?.lead_product_relation_display_id}`)
        }
        className="mt-auto flex items-center gap-1 text-blue-600 hover:underline text-sm font-medium"
      >
        <FiEye className="w-4 h-4" />
        View
      </button>
    </div>
  );
};


  return (
    <div className="page-container flex flex-col flex-1">
      <div className="flex flex-col flex-1 gap-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-blue-600">Leads</h1>
          <div className="flex gap-3">
            <select
              value={selectedStatus}
              disabled={
                !Boolean(
                  !loggedInUser?.isSkipLogin && loggedInUser?.isAuthenticated
                )
              }
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-lg p-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="New">New</option>
              <option value="Assigned">Assigned</option>
              <option value="Sold">Sold</option>
              <option value="Lost">Lost</option>
            </select>
            <button
              disabled={
                !Boolean(
                  !loggedInUser?.isSkipLogin && loggedInUser?.isAuthenticated
                )
              }
              onClick={async () => {
                if (
                  loggedInUser?.user?.advisor_onboarding_status_id?.title ===
                  "Approved"
                ) {
                  setIsAddLeadOpen(true);
                } else {
                  const confirmed = await AdvisorConditionsModal.show(navigate);
                  console.log("confirmed", confirmed);
                }
              }}
              className="flex items-center gap-1.5 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              Add
            </button>
            <GridListToggle viewMode={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {loggedInUser?.isSkipLogin || !loggedInUser?.isAuthenticated ? (
          <AdvisorConditionsPage type="Skip/NotLogin" />
        ) : (
          <>
            {viewMode === "list" && (
              <ServerPaginationDataGrid
                url={getAdvisorLeadsUrl}
                requestParameter={{
                  status: selectedStatus !== "Status" ? selectedStatus : "",
                }}
                limit={10}
                columns={columns}
                isServerPagination={true}
                key={"lead_id"}
                dataMapper={(list) => list}
                height={"calc(100vh - 350px)"}
              />
            )}
            {viewMode === "grid" && (
              <InfiniteScrollWrapper
                url={getAdvisorLeadsUrl}
                params={{
                  status: selectedStatus !== "Status" ? selectedStatus : "",
                }}
                pageLimit={10}
                height="calc(100vh - 18rem)"
                scrollableDivClassName="max-h-[calc(100vh_-_18rem)]"
                ItemComp={LeadCard}
                gap={"1.5rem"}
                size={{ xxs: 12, xs: 12, md: 6, lg: 6 }}
              />
            )}
          </>
        )}

        {/* Add Lead Popup */}
        {isAddLeadOpen && (
          <AddLeadModal
            isOpen={isAddLeadOpen}
            onClose={() => setIsAddLeadOpen(false)}
            // onLeadAdded={}
          />
        )}
      </div>
    </div>
  );
};

export default LeadsTab;
